import {
  getVendorAccountErrorCode,
  getVendorAccountErrorMessage,
  lockboxAccountsApi,
  type CreateVendorAccountPayload as CreateAccountPayload
} from "api/lockbox-accounts";
import { getHubSpotAuthorizationUrl } from "api/landlord/landlord";
import { igloohomeApi } from "api/igloohome";
import { useState } from "react";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  IntegrationId,
  Step
} from "views/settings-integration/components/AddIntegrationModal/types";
import { useSettingsController } from "views/settings-integration/controller";
import { useAuthContext } from "providers/Auth";

const LockboxAccountForm = ({
  idPrefix,
  name,
  username,
  password,
  onNameChange,
  onUsernameChange,
  onPasswordChange,
  onBack,
  onSubmit,
  isConnecting
}: {
  idPrefix: string;
  name: string;
  username: string;
  password: string;
  onNameChange: (v: string) => void;
  onUsernameChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  isConnecting: boolean;
}) => (
  <>
    <div className="space-y-4">
      <BaseInput
        label="Account name"
        required
        id={`${idPrefix}-name`}
        placeholder="e.g. Downtown Portfolio"
        maxLength={255}
        value={name}
        onChange={onNameChange}
      />
      <BaseInput
        label="Username"
        required
        id={`${idPrefix}-username`}
        placeholder="Username"
        maxLength={255}
        value={username}
        onChange={onUsernameChange}
      />
      <BaseInput
        label="Password"
        required
        id={`${idPrefix}-password`}
        type="password"
        placeholder="**********************"
        maxLength={255}
        value={password}
        onChange={onPasswordChange}
      />
    </div>
    <div className="flex justify-end gap-2 mt-4">
      <Button
        variant={ButtonVariant.BASE}
        size={ButtonSize.BASE}
        onClick={onBack}
        className="w-[125px]"
      >
        Back
      </Button>
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.BASE}
        onClick={onSubmit}
        loading={isConnecting}
        disabled={!name || !username || !password}
        className="w-[125px]"
      >
        Continue
      </Button>
    </div>
  </>
);

const IgloohomeAccountForm = ({
  name,
  nameError,
  onNameChange,
  onBack,
  onSubmit,
  isConnecting
}: {
  name: string;
  nameError: string;
  onNameChange: (v: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  isConnecting: boolean;
}) => (
  <>
    <p className="text-sm text-content-500">
      Name this account, then you&apos;ll be redirected to Igloohome to log in
      and authorize Boom.
    </p>
    <BaseInput
      label="Account name"
      required
      id="igloohome-name"
      placeholder="e.g. Main House"
      maxLength={255}
      value={name}
      error={nameError}
      onChange={onNameChange}
      className="mt-4"
    />
    <div className="flex justify-end gap-2 mt-4">
      <Button
        variant={ButtonVariant.BASE}
        size={ButtonSize.BASE}
        onClick={onBack}
        className="w-[125px]"
      >
        Back
      </Button>
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.BASE}
        onClick={onSubmit}
        loading={isConnecting}
        disabled={!name}
        className="w-[125px]"
      >
        Continue
      </Button>
    </div>
  </>
);

interface SecondStepProps {
  setCurrentStep: (step: Step) => void;
  closeModal: () => void;
  selectedIntegration: string;
  onConnectSuccess?: (integrationId: IntegrationId) => void;
}

export const SecondStep = ({
  setCurrentStep,
  closeModal,
  selectedIntegration,
  onConnectSuccess
}: SecondStepProps) => {
  const { updateSettings, isUpdateSettingsPending } = useSettingsController();
  const { refreshPartnerData } = useAuthContext();
  const queryClient = useQueryClient();

  const [googleAnalyticsId, setGoogleAnalyticsId] = useState("");
  const [googleAdsConversionId, setGoogleAdsConversionId] = useState("");
  const [googleAdsContactFormLabel, setGoogleAdsContactFormLabel] =
    useState("");
  const [googleAdsShowingLabel, setGoogleAdsShowingLabel] = useState("");
  const [codeboxName, setCodeboxName] = useState("");
  const [codeboxUsername, setCodeboxUsername] = useState("");
  const [codeboxPassword, setCodeboxPassword] = useState("");
  const [sentriLockName, setSentriLockName] = useState("");
  const [sentriLockUsername, setSentriLockUsername] = useState("");
  const [sentriLockPassword, setSentriLockPassword] = useState("");
  const [igloohomeName, setIgloohomeName] = useState("");
  const [igloohomeNameError, setIgloohomeNameError] = useState("");
  const [rentlyName, setRentlyName] = useState("");
  const [rentlyUsername, setRentlyUsername] = useState("");
  const [rentlyPassword, setRentlyPassword] = useState("");

  const { mutate: connectCodebox, isPending: isConnectingCodebox } =
    useMutation({
      mutationKey: ["LOCKBOX_ACCOUNTS", "codebox", "CREATE"],
      mutationFn: (payload: CreateAccountPayload) =>
        lockboxAccountsApi.create("codebox", payload),
      onError: (err) => {
        toast.error(getVendorAccountErrorMessage(err));
      }
    });
  const { mutate: connectSentriLock, isPending: isConnectingSentriLock } =
    useMutation({
      mutationKey: ["LOCKBOX_ACCOUNTS", "sentrilock", "CREATE"],
      mutationFn: (payload: CreateAccountPayload) =>
        lockboxAccountsApi.create("sentrilock", payload),
      onError: (err) => {
        toast.error(getVendorAccountErrorMessage(err));
      }
    });

  const { mutate: connectRently, isPending: isConnectingRently } = useMutation({
    mutationKey: ["LOCKBOX_ACCOUNTS", "rently", "CREATE"],
    mutationFn: (payload: CreateAccountPayload) =>
      lockboxAccountsApi.create("rently", payload),
    onError: (err) => {
      toast.error(getVendorAccountErrorMessage(err));
    }
  });

  const { mutate: startIgloohomeConnect, isPending: isConnectingIgloohome } =
    useMutation({
      mutationKey: ["IGLOOHOME", "START_CONNECT"],
      mutationFn: () => igloohomeApi.startConnect(igloohomeName.trim()),
      onSuccess: ({ authorization_url }) => {
        window.location.assign(authorization_url);
      },
      onError: (err) => {
        const message = getVendorAccountErrorMessage(err);
        if (getVendorAccountErrorCode(err) === "duplicate_name")
          setIgloohomeNameError(message);
        else toast.error(message);
      }
    });

  const handleAuthorizeHubSpot = async () => {
    const data = await getHubSpotAuthorizationUrl();
    window.open(data.authorization_url, "HubSpot Auth");
  };

  return (
    <>
      <Show if={selectedIntegration === IntegrationId.GA}>
        <BaseInput
          label="GA account ID"
          required
          id="ga-account-id"
          placeholder="G-XYZ"
          value={googleAnalyticsId}
          onChange={setGoogleAnalyticsId}
        />
        <div className="flex justify-end gap-2 mt-4">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.BASE}
            onClick={() => setCurrentStep(Step.FIRST)}
          >
            Cancel
          </Button>
          <Button
            loading={isUpdateSettingsPending}
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            onClick={() => {
              updateSettings({ googleAnalyticsId }).then(() => closeModal());
            }}
            disabled={!googleAnalyticsId}
          >
            Continue
          </Button>
        </div>
      </Show>

      <Show if={selectedIntegration === IntegrationId.GOOGLE_ADS}>
        <div className="flex flex-col gap-4">
          <BaseInput
            label="Conversion ID"
            required
            id="google-ads-conversion-id"
            placeholder="AW-123456789"
            value={googleAdsConversionId}
            onChange={setGoogleAdsConversionId}
          />
          <BaseInput
            label="Conversion Label — Contact Form Submitted"
            id="google-ads-contact-form-label"
            placeholder="AbCdEfGhIj"
            value={googleAdsContactFormLabel}
            onChange={setGoogleAdsContactFormLabel}
          />
          <BaseInput
            label="Conversion Label — Showing Booked"
            id="google-ads-showing-label"
            placeholder="KlMnOpQrSt"
            value={googleAdsShowingLabel}
            onChange={setGoogleAdsShowingLabel}
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.BASE}
            onClick={() => setCurrentStep(Step.FIRST)}
          >
            Cancel
          </Button>
          <Button
            loading={isUpdateSettingsPending}
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            onClick={() => {
              // Trim before saving: a trailing space (common when copying from
              // the Google Ads "Tag setup" panel) would fail the LX charset
              // check and silently disable conversion reporting.
              updateSettings({
                googleAdsConversionId: googleAdsConversionId.trim(),
                googleAdsContactFormConversionLabel:
                  googleAdsContactFormLabel.trim() || null,
                googleAdsShowingConversionLabel:
                  googleAdsShowingLabel.trim() || null
              }).then(() => closeModal());
            }}
            disabled={
              !googleAdsConversionId.trim() ||
              (!googleAdsContactFormLabel.trim() &&
                !googleAdsShowingLabel.trim())
            }
          >
            Continue
          </Button>
        </div>
      </Show>

      <Show if={selectedIntegration === IntegrationId.HUBSPOT}>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          onClick={handleAuthorizeHubSpot}
        >
          Connect to HubSpot
        </Button>
      </Show>

      <Show if={selectedIntegration === IntegrationId.CODEBOX}>
        <LockboxAccountForm
          idPrefix="codebox"
          name={codeboxName}
          username={codeboxUsername}
          password={codeboxPassword}
          onNameChange={setCodeboxName}
          onUsernameChange={setCodeboxUsername}
          onPasswordChange={setCodeboxPassword}
          onBack={() => setCurrentStep(Step.FIRST)}
          onSubmit={() =>
            connectCodebox(
              {
                name: codeboxName.trim(),
                username: codeboxUsername.trim(),
                password: codeboxPassword
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: ["LOCKBOX_ACCOUNTS", "codebox"]
                  });
                  refreshPartnerData();
                  closeModal();
                  onConnectSuccess?.(IntegrationId.CODEBOX);
                }
              }
            )
          }
          isConnecting={isConnectingCodebox}
        />
      </Show>

      <Show if={selectedIntegration === IntegrationId.SENTRILOCK}>
        <LockboxAccountForm
          idPrefix="sentrilock"
          name={sentriLockName}
          username={sentriLockUsername}
          password={sentriLockPassword}
          onNameChange={setSentriLockName}
          onUsernameChange={setSentriLockUsername}
          onPasswordChange={setSentriLockPassword}
          onBack={() => setCurrentStep(Step.FIRST)}
          onSubmit={() =>
            connectSentriLock(
              {
                name: sentriLockName.trim(),
                username: sentriLockUsername.trim(),
                password: sentriLockPassword
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: ["LOCKBOX_ACCOUNTS", "sentrilock"]
                  });
                  refreshPartnerData();
                  closeModal();
                }
              }
            )
          }
          isConnecting={isConnectingSentriLock}
        />
      </Show>

      <Show if={selectedIntegration === IntegrationId.IGLOOHOME}>
        <IgloohomeAccountForm
          name={igloohomeName}
          nameError={igloohomeNameError}
          onNameChange={(value) => {
            setIgloohomeName(value);
            if (igloohomeNameError) setIgloohomeNameError("");
          }}
          onBack={() => setCurrentStep(Step.FIRST)}
          onSubmit={startIgloohomeConnect}
          isConnecting={isConnectingIgloohome}
        />
      </Show>

      <Show if={selectedIntegration === IntegrationId.RENTLY_LOCKBOXES}>
        <LockboxAccountForm
          idPrefix="rently"
          name={rentlyName}
          username={rentlyUsername}
          password={rentlyPassword}
          onNameChange={setRentlyName}
          onUsernameChange={setRentlyUsername}
          onPasswordChange={setRentlyPassword}
          onBack={() => setCurrentStep(Step.FIRST)}
          onSubmit={() =>
            connectRently(
              {
                name: rentlyName.trim(),
                username: rentlyUsername.trim(),
                password: rentlyPassword
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: ["LOCKBOX_ACCOUNTS", "rently"]
                  });
                  refreshPartnerData();
                  closeModal();
                }
              }
            )
          }
          isConnecting={isConnectingRently}
        />
      </Show>
    </>
  );
};
